import React, { useEffect, useState } from "react";
import { useLang } from "../i18n";

/* Patient reviews on the homepage (Nov 2026): a live feed of the practice's Google reviews.

   How it works
   - The saved (prerendered) page shows the rating, the count and a "Read all our reviews on Google" button,
     from the typed-in values below. No review text is ever saved in the page or the repo.
   - When the page loads in a browser it asks /api/google-reviews (netlify/functions/google-reviews.mjs),
     which gets the live rating, count and up to 5 reviews from Google's Places API. Google chooses those 5
     ("most relevant"); we cannot pick or hide individual reviews.
   - If that call fails or is not set up yet, the section stays as the saved version. Nothing breaks.

   Google's display rules, all followed here: each review shows the author's name and photo as Google gives
   them, linked to their Google profile; each review links to itself on Google Maps; the section says
   "Reviews from Google"; the text is shown as written (only shortened, with a link to the full review).

   FALLBACK_RATING / FALLBACK_COUNT: last values typed in by hand (Oct 11, 2026). Only used until the live
   numbers load, so they do not need regular updates. */
const FALLBACK_RATING = 5.0;
const FALLBACK_COUNT = 18;
/* How many of Google's reviews to show, in Google's own order ("most relevant"). Google's rules ask for a notice
   saying how reviews are ordered and filtered; NOTICE below says it. */
const MAX_SHOWN = 4;
export const GOOGLE_REVIEWS_URL = "https://www.google.com/maps?cid=11525053104814632771";

const T = {
  en: {
    sub: "Patient Reviews",
    title: "What Our Patients Say",
    line: (n) => <>Average rating from <b>{n} reviews on Google</b></>,
    stars: (r) => `${r} out of 5 stars`,
    more: "Read the full review on Google",
    all: "Read all our reviews on Google",
    from: "Reviews from Google",
    notice: (n) => `Showing ${n} reviews Google ranks as most relevant.`,
  },
  es: {
    sub: "Opiniones de Pacientes",
    title: "Lo Que Dicen Nuestros Pacientes",
    line: (n) => <>Calificación promedio de <b>{n} opiniones en Google</b></>,
    stars: (r) => `${r} de 5 estrellas`,
    more: "Leer la opinión completa en Google",
    all: "Lea todas nuestras opiniones en Google",
    from: "Opiniones de Google",
    notice: (n) => `Se muestran ${n} opiniones que Google considera más relevantes.`,
  },
};

const STYLES = `
  .rv-sec { background: var(--c-white); padding: clamp(60px, 10vw, 100px) clamp(16px, 3vw, 24px); }
  .rv-wrap { max-width: 1120px; margin: 0 auto; text-align: center; }
  .rv-sub { font-family: var(--body); font-size: 14px; font-weight: 600; color: var(--c-gold); letter-spacing: 4px; text-transform: uppercase; margin: 0 0 10px; }
  .rv-title { font-family: var(--head); font-size: clamp(28px, 4vw, 44px); color: var(--c-navy); font-weight: 600; margin: 0; line-height: 1.2; }
  .rv-rule { width: 50px; height: 2px; background: var(--c-gold); margin: 18px auto 36px; }

  .rv-score { display: inline-flex; align-items: center; gap: 20px; text-align: left; }
  .rv-num { font-family: var(--head); font-size: clamp(60px, 8vw, 84px); font-weight: 600; color: var(--c-navy); line-height: 1; font-variant-numeric: lining-nums; }
  .rv-stars { display: inline-flex; gap: 4px; vertical-align: middle; }
  .rv-stars svg { display: block; }
  .rv-line { font-family: var(--body); font-size: 16px; color: var(--c-text-muted); line-height: 1.6; margin: 6px 0 0; }
  .rv-line b { color: var(--c-navy); font-weight: 600; }

  .rv-grid { display: flex; flex-wrap: wrap; justify-content: center; gap: 24px; list-style: none; margin: 48px 0 0; padding: 0; text-align: left; }
  .rv-grid { max-width: 900px; margin-left: auto; margin-right: auto; }
  .rv-grid li { display: flex; flex: 0 1 calc((100% - 24px) / 2); min-width: 0; }
  .rv-card { margin: 0; width: 100%; display: flex; flex-direction: column; background: var(--c-bg); border: 1px solid var(--c-border); padding: 26px 24px 22px; }
  .rv-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
  .rv-av { width: 40px; height: 40px; border-radius: 50%; flex: none; background: var(--c-bg-alt); object-fit: cover; }
  .rv-av-ph { display: flex; align-items: center; justify-content: center; font-family: var(--head); font-size: 20px; font-weight: 600; color: var(--c-navy); }
  .rv-who { font-family: var(--body); font-size: 15px; font-weight: 600; color: var(--c-navy); text-decoration: none; display: block; }
  a.rv-who:hover { text-decoration: underline; }
  .rv-when { font-family: var(--body); font-size: 13px; color: var(--c-text-muted); margin-top: 2px; display: flex; align-items: center; gap: 8px; }
  .rv-text { margin: 0 0 16px; font-family: var(--body); font-size: 15px; line-height: 1.7; color: var(--c-text-body); flex: 1;
             display: -webkit-box; -webkit-line-clamp: 7; -webkit-box-orient: vertical; overflow: hidden; white-space: pre-line; }
  .rv-more { font-family: var(--body); font-size: 13px; font-weight: 600; color: var(--c-navy); text-decoration: underline; text-underline-offset: 3px; align-self: flex-start; }

  .rv-foot { margin-top: 36px; display: flex; flex-direction: column; align-items: center; gap: 14px; }
  .rv-btn { display: inline-block; font-family: var(--body); font-size: 14px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; text-decoration: none; color: var(--c-navy); border: 1.5px solid var(--c-navy); padding: 14px 28px; transition: background 0.25s ease, color 0.25s ease; }
  .rv-btn:hover { background: var(--c-navy); color: var(--c-white); }
  .rv-attr { font-family: 'Roboto', Arial, sans-serif; font-size: 12px; color: var(--c-text-muted); margin: 0; }

    @media (max-width: 600px) {
    .rv-grid { gap: 16px; } .rv-grid li { flex-basis: 100%; }
    .rv-score { flex-direction: column; text-align: center; gap: 10px; }
    .rv-btn { display: block; width: 100%; box-sizing: border-box; }
  }
`;

const STAR = "M12 2.5l2.9 6.1 6.6.9-4.8 4.6 1.2 6.6L12 17.6l-5.9 3.1 1.2-6.6L2.5 9.5l6.6-.9L12 2.5z";

function Stars({ value, size, label }) {
  const full = Math.round(value || 0);
  return (
    <span className="rv-stars" role="img" aria-label={label}>
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path fill={i < full ? "var(--c-gold)" : "var(--c-border)"} d={STAR} />
        </svg>
      ))}
    </span>
  );
}

function Avatar({ author }) {
  const [broken, setBroken] = useState(false);
  if (author.photo && !broken) {
    return <img className="rv-av" src={author.photo} alt="" width="40" height="40" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
  }
  return <span className="rv-av rv-av-ph" aria-hidden="true">{(author.name || "?").trim().charAt(0).toUpperCase()}</span>;
}

function ReviewCard({ r, t }) {
  const name = r.author.url
    ? <a className="rv-who" href={r.author.url} target="_blank" rel="noopener noreferrer nofollow">{r.author.name}</a>
    : <span className="rv-who">{r.author.name}</span>;
  return (
    <figure className="rv-card">
      <figcaption className="rv-head">
        <Avatar author={r.author} />
        <div>
          {name}
          <span className="rv-when"><Stars value={r.rating} size={14} label={t.stars(r.rating)} />{r.when}</span>
        </div>
      </figcaption>
      <blockquote className="rv-text" lang="en">{r.text}</blockquote>
      {r.url ? <a className="rv-more" href={r.url} target="_blank" rel="noopener noreferrer nofollow">{t.more}</a> : null}
    </figure>
  );
}

export default function Reviews() {
  const t = T[useLang() === "es" ? "es" : "en"];
  const [live, setLive] = useState(null);

  useEffect(() => {
    let gone = false;
    fetch("/api/google-reviews")
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => { if (!gone && d && !d.error) setLive(d); })
      .catch(() => {});
    return () => { gone = true; };
  }, []);

  const rating = live && live.rating ? live.rating : FALLBACK_RATING;
  const count = live && live.count ? live.count : FALLBACK_COUNT;
  const reviews = ((live && live.reviews) || []).slice(0, MAX_SHOWN);
  const allUrl = (live && live.mapsUrl) || GOOGLE_REVIEWS_URL;
  const shown = Number(rating).toFixed(1);

  return (
    <section id="reviews" className="rv-sec" aria-labelledby="rv-title">
      <style>{STYLES}</style>
      <div className="rv-wrap">
        <p className="rv-sub">{t.sub}</p>
        <h2 id="rv-title" className="rv-title">{t.title}</h2>
        <div className="rv-rule" />
        <div className="rv-score">
          <span className="rv-num">{shown}</span>
          <div>
            <Stars value={rating} size={24} label={t.stars(shown)} />
            <p className="rv-line">{t.line(count)}</p>
          </div>
        </div>
        {reviews.length ? (
          <ul className="rv-grid">
            {reviews.map((r, i) => <li key={i}><ReviewCard r={r} t={t} /></li>)}
          </ul>
        ) : null}
        <div className="rv-foot">
          <a className="rv-btn" href={allUrl} target="_blank" rel="noopener noreferrer">{t.all}</a>
          <p className="rv-attr">{t.from}{reviews.length ? <> · {t.notice(reviews.length)}</> : null}</p>
        </div>
      </div>
    </section>
  );
}
